//Feb25 CustomFieldWrapper.jsx
//Feb 13 CustomFieldWrapper with text new transformation logic
import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TextField, Autocomplete, useTheme } from '@mui/material';
import { styled } from '@mui/system';
import { useFormTheme } from '../../contexts/FormThemeContext'; // Adjust path as necessary
//import DatePicker from 'react-multi-date-picker';


const CustomFieldWrapperBase = forwardRef(({
  autoFocus,
  variant,
  customStyles,
  size,
  focusColor,
  disabled,
  error,
  dropdownOptions, // Options mapped to optionsSetUFI by FieldRenderer
  renderInput, // For custom input rendering logic
  inputRef, // To forward the ref
  InputProps, // To pass down InputProps  
  placeholder, // To handle placeholder for hints
  helperText, // Regular helper text
  externalHelperText, // For validation errors
  multiple = false, // Determines if Autocomplete should allow multi-selection (default to single)
  label,
  required,
  maxLength,
  hidden,
  transformations,
  value,
  isOptionEqualToValue,
  multiline = false, // Enable multi-line functionality
  rows = 1, // Default rows to 1 for single-line inputs, update if multi-line
  onChange, 
  ...props
}, ref) => {
//console.log("[CustomFieldWrapper] Received props:", props);
//console.log("[CustomFieldWrapper] Extracted fieldId:", props.fieldId);
const { fieldId, ...validProps } = props; // ✅ Remove fieldId before spreading

  const theme = useTheme();
  const { colors, fonts } = useFormTheme(); // Using form theme context

  // Memoize variantStyles to avoid recalculating unless dependencies change
  const variantStyles = useMemo(
    () => getVariantStyles(variant, theme, customStyles, focusColor, disabled, error, colors, fonts),
    [variant, theme, customStyles, focusColor, disabled, error, colors, fonts]
  );

  // Update isAutocomplete to rely on dropdownOptions, passed from FieldRenderer
const isAutocomplete = Array.isArray(dropdownOptions) && dropdownOptions.length > 0;

  const effectiveHelperText = error ? externalHelperText : helperText; // Use externalHelperText in error state


// Modify handleChange for TextField
const handleTextFieldChange = (event) => {
  if (!onChange) {
    console.warn(`[CustomFieldWrapper] onChange is undefined for fieldId ${props.fieldId}`);
    return;
  }

//  console.log(`[CustomFieldWrapper] handleTextFieldChange - Received input for fieldId ${props.fieldId}:`, event.target.value);

  // ✅ Ensure transformed value is being passed correctly
//  console.log(`[CustomFieldWrapper] handleTextFieldChange - Forwarding event with value:`, event.target.value);

  onChange(event); // Directly pass the event up
};


/* ────────────────────────────────────────────────────────────
   🆕 Derive option object(s) from `value` for Autocomplete UI
   - `value` is the canonical optionId (string) or array of ids
   - Autocomplete wants an option object (or array of them)
   - We map ids → `dropdownOptions` objects for display
   ──────────────────────────────────────────────────────────── */
const selectedAutocompleteValue = useMemo(() => {
  if (!isAutocomplete) return multiple ? [] : null;

  // Guard: dropdownOptions must be an array of { optionId, label, ... }
  const opts = Array.isArray(dropdownOptions) ? dropdownOptions : [];

  if (multiple) {
    const ids = Array.isArray(value) ? value : (value ? [value] : []);
    const selectedList = opts.filter(opt => ids.includes(opt.optionId));
    return selectedList;
  } else {
    if (!value) return null;
    const selectedOne = opts.find(opt => opt.optionId === value) || null;
    return selectedOne;
  }
}, [isAutocomplete, multiple, value, dropdownOptions]);

const effectiveAutocompletePlaceholder = useMemo(() => {
  // Enterprise UX rule:
  // - For multi-select autocomplete, only show placeholder when nothing is selected
  // - For single-select autocomplete, preserve normal placeholder behavior
  if (!isAutocomplete) return placeholder || '';

  if (multiple) {
    const selectedList = Array.isArray(selectedAutocompleteValue)
      ? selectedAutocompleteValue
      : [];
    return selectedList.length > 0 ? '' : (placeholder || '');
  }

  return placeholder || '';
}, [isAutocomplete, multiple, selectedAutocompleteValue, placeholder]);

if (isAutocomplete) {
  return (
    <Autocomplete
      {...validProps}
      options={dropdownOptions}
      multiple={multiple}
      disabled={disabled}
      disableCloseOnSelect={multiple}

            isOptionEqualToValue={
        isOptionEqualToValue ||
        ((option, value) => {
          if (!option || !value) return false;
          const vId = typeof value === 'string' ? value : value.optionId;
          return option.optionId === vId;
        })
      }

      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option?.label || ''
      }
      value={selectedAutocompleteValue}

      componentsProps={{
        popper: {
          modifiers: [{ name: 'preventOverflow', options: { boundary: 'window' } }],
          sx: { minWidth: 'fit-content', maxWidth: 'none' },
        },
        paper: {
          sx: {
            color: colors.textColor,
            padding: 0,
            margin: 0,
            minWidth: 'fit-content',
            width: 'auto',
          },
        },
      }}

      renderOption={(listItemProps, option) => {
        const { key, ...restProps } = listItemProps;
        return (
          <li
            key={option.optionId}
            {...restProps}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: '8px 12px',
              fontFamily: fonts.secondaryFont,
            }}
          >
            <span>{option.label}</span>
            {option.hint && (
              <span style={{ fontSize: '0.85em', color: 'gray', marginLeft: '8px' }}>
                {option.hint}
              </span>
            )}
          </li>
        );
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          // 👇 attach the actual HTMLInputElement ref here
          inputRef={inputRef}
          // If you want to preserve the outer ref as well, you can also do:
          // ref={ref}
          size={size}
          variant={variant}
          disabled={disabled}
          error={error}
          onFocus={validProps?.onFocus}
          onBlur={validProps?.onBlur}
          helperText={effectiveHelperText}
          sx={{ ...variantStyles, ...props.sx }}
          InputProps={{
            ...params.InputProps,
            ...InputProps,
            style: {
              ...params.InputProps?.style,
              ...InputProps?.style,
              color: colors.textColor,
              fontFamily: fonts.secondaryFont,
              backgroundColor: disabled ? 'rgba(0, 0, 0, 0.06)' : undefined,
            },
          }}
          placeholder={effectiveAutocompletePlaceholder}
        />
      )}


      onChange={(event, value, reason, details) => {
        if (!onChange) return;
        if (multiple) {
          const optionIds = Array.isArray(value)
            ? value.map(v => (typeof v === 'string' ? v : v?.optionId)).filter(Boolean)
            : [];
          onChange({ target: { value: optionIds } }, value, reason, details);
        } else {
          const optionId = value
            ? (typeof value === 'string' ? value : value.optionId)
            : '';
          onChange({ target: { value: optionId } }, value, reason, details);
        }
      }}
    />
  );
}

// (TextField fallback remains unchanged)
return (
  <TextField
    {...validProps}
    onChange={handleTextFieldChange}
    onFocus={validProps?.onFocus}
    onBlur={validProps?.onBlur} 
    required={required}
    label={label}
    ref={ref}                 // root ref
    inputRef={inputRef}       // 👈 HTMLInputElement ref for MUI X
    variant={variant}
    size={size}
    disabled={disabled}
    error={error}
    helperText={effectiveHelperText}
    placeholder={placeholder}
    value={value}
    sx={{ ...variantStyles, ...props.sx }}
    inputProps={{
      ...InputProps,
      ...(maxLength ? { maxLength } : {}),
    }}
    InputProps={{
      ...InputProps,
      style: {
        ...InputProps?.style,
        color: colors.textColor,
        fontFamily: fonts.secondaryFont,
        backgroundColor: disabled ? 'rgba(0, 0, 0, 0.06)' : undefined,
      },
    }}
    autoFocus={autoFocus}
    multiline={multiline}
    rows={multiline ? rows : undefined}
  />
);


});


const getVariantStyles = (variant, theme, customStyles, focusColor, disabled, error, colors, fonts) => {
  let baseStyles = {
    '& .Mui-disabled': {
      color: theme.palette.text.disabled,
      '& .MuiOutlinedInput-notchedOutline, & .MuiFilledInput-underline:before, & .MuiInput-underline:before': {
        borderColor: theme.palette.action.disabledBackground,
      },
      '& .MuiInputLabel-root': {
        color: theme.palette.text.disabled,
      },
    },
    ...(error && {
      color: theme.palette.error.main,
      '& .MuiOutlinedInput-notchedOutline, & .MuiFilledInput-underline:before, & .MuiInput-underline:before, & .MuiInput-underline:after': {
        borderColor: theme.palette.error.main,
      },
      '& .MuiInputLabel-root': {
        color: theme.palette.error.main,
      },
    }),
    // Apply theme-dependent styles for all variants
    color: colors.textColor,
    fontFamily: fonts.secondaryFont, // Use secondaryFont for input text by default
    '& .MuiInputLabel-root': {
      color: colors.textColor,
      fontFamily: fonts.primaryFont, // Use primaryFont for labels
      '&.Mui-focused': {
        color: focusColor || colors.activeColor, // Apply active color when focused
      },
    },
    '& .MuiOutlinedInput-root': {
      borderColor: colors.activeColor,
      '&:hover fieldset': {
        borderColor: colors.activeColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: focusColor || colors.activeColor,
      },
    },
    '& .MuiFilledInput-root': {
      backgroundColor: colors.backgroundColor,
      '&:hover': {
        backgroundColor: colors.backgroundColor,
      },
      '&.Mui-focused': {
        backgroundColor: colors.backgroundColor,
        borderColor: focusColor || colors.activeColor,
        boxShadow: `0px 0px 0px 2px ${focusColor || colors.activeColor}`, // Optional: add focus shadow
      },
    },
    '& .MuiInput-root': {
      borderBottom: `2px solid ${colors.activeColor}`,
      '&:hover:not(.Mui-disabled):before': {
        borderBottom: `2px solid ${colors.activeColor}`,
      },
      '&.Mui-focused:before': {
        borderBottom: `2px solid ${focusColor || colors.activeColor}`,
      },
      '&.MuiInput-underline:after': {
        borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color after input
      },
    },
    // Apply custom styles if provided
    ...customStyles,
  };

  // Apply variant-specific styles
  switch (variant) {
    case 'outlined':
      return {
        ...baseStyles,
        '& .MuiOutlinedInput-root': {
          borderColor: focusColor || colors.activeColor,
          '&:hover fieldset': {
            borderColor: colors.activeColor,
          },
          '&.Mui-focused fieldset': {
            borderColor: focusColor || colors.activeColor,
          },
        },
      };
    case 'filled':
      return {
        ...baseStyles,
        '& .MuiFilledInput-root': {
          backgroundColor: theme.palette.grey[100],
          '&:hover': {
            backgroundColor: theme.palette.grey[200],
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.grey[100],
            borderColor: focusColor || colors.activeColor,
            boxShadow: `0px 0px 0px 2px ${focusColor || colors.activeColor}`, // Optional: add focus shadow
          },
          '&:before': {
            borderBottom: `2px solid transparent`, // Hide default blue bottom border
          },
          '&:after': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color for the bottom border
          },
        },
      };
    case 'standard':
      return {
        ...baseStyles,
        '& .MuiInput-root': {
          '&:before': {
            borderBottom: `1px solid ${theme.palette.divider}`, // Default unfocused color
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Active color on hover
          },
          '&.Mui-focused:before': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Active color on focus
          },
          '&.MuiInput-underline:after': {
            borderBottom: `2px solid ${focusColor || colors.activeColor}`, // Ensure active color after input
          },
        },
      };
    default:
      return baseStyles;
  }
};

const CustomFieldWrapper = styled(CustomFieldWrapperBase)(({ theme }) => ({
  width: '100%',
}));

CustomFieldWrapper.propTypes = {
  autoFocus: PropTypes.bool,
  variant: PropTypes.oneOf(['standard', 'filled', 'outlined']),
  customStyles: PropTypes.object,
  size: PropTypes.oneOf(['small', 'medium']),
  focusColor: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool, // Ensure error is defined in propTypes
  helperText: PropTypes.string, // Regular helper text
  externalHelperText: PropTypes.string, // Validation error helper text
  dropdownOptions: PropTypes.array, // Updated from options to dropdownOptions
  multiple: PropTypes.bool, // Added for multi-select control
  renderInput: PropTypes.func,
  inputRef: PropTypes.oneOfType([
    PropTypes.func, 
    PropTypes.shape({ current: PropTypes.any })
  ]),
  InputProps: PropTypes.object,
  placeholder: PropTypes.string,
  isOptionEqualToValue: PropTypes.func, // Ensure isOptionEqualToValue is defined in propTypes
  multiline: PropTypes.bool, // New prop for multi-line support
  rows: PropTypes.number, // New prop to specify rows in multi-line mode
  onChange: PropTypes.func.isRequired,
};


export default CustomFieldWrapper;
